import React, { useEffect } from 'react';
import { useTable, usePagination, useSortBy, useRowSelect } from 'react-table';
import { sortUp, sortDown, organisation } from '@ibinder/common-icons';
import styled from 'styled-components';
import {
  Card,
  Flexbox,
  Pagination,
  SingleSelect
} from '@ibinder/common-frontend';
import IndeterminateCheckbox from './IndeterminateCheckbox';

import {
  TableContainer,
  Table,
  HiddenSVGPlaceHolder,
  TableRow,
  TableHeadItem,
  TableHeadContent,
  TableHead,
  TableData,
  SortContainer
} from './styles.index';

import { GetFlag } from '../OrganizationViewer/components/GetFlag';
import { FlagFlexbox } from '../OrganizationViewer/OrganizationInfo';

const InnerTableData = styled(Flexbox)`
  align-items: center;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  svg {
    min-width: 3rem;
  }
`;
const StyledSingleSelect = styled(SingleSelect)`
  margin-left: auto;
`;
const PaginationContainer = styled(Flexbox)`
  align-items: center;
`;

const ListDocument = ({
  data,
  onSort,
  sortBy,
  columns,
  selectedIds,
  setSelectedIds,
  hasSelectBox = true,
  fetchMore,
  hasFetchMore,
  loading,
  type
}) => {
  const getSelectedIndices = {
    ...data.map((r) => {
      if (selectedIds.find((e) => e === r.id)) {
        return true;
      }
    })
  };
  const tableData = React.useMemo(() => data, [data, sortBy]);
  const tableColumns = React.useMemo(() => columns, [columns, sortBy]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    toggleSortBy,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state: { pageIndex, pageSize, selectedRowIds }
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      autoResetPage: false,
      initialState: {
        pageSize: 10,
        pageIndex: 0,
        selectedRowIds: selectedIds.length > 0 ? getSelectedIndices : {}
      }
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (hasSelectBox) {
        hooks.visibleColumns.push((column) => [
          {
            id: 'selection',
            groupByBoundary: true,
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => {
              return (
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              );
            }
          },
          ...column
        ]);
      }
    }
  );

  useEffect(() => {
    //Careful with update if selectIds are same as we are about to set.
    if (
      JSON.stringify(selectedIds) !==
      JSON.stringify(
        Object.keys(selectedRowIds)
          .filter((e) => selectedRowIds[e])
          .map((i) => data[i].id)
      )
    ) {
      setSelectedIds(
        Object.keys(selectedRowIds)
          .filter((e) => selectedRowIds[e])
          .map((i) => {
            return [
              'Binders',
              'DeletedBinders'
              // TODO: Use when backend is ready
              // 'DeletedBindersEU'
            ].includes(type)
              ? data[i].binder.id
              : data[i].id;
          })
      );
    }
  }, [setSelectedIds, selectedRowIds]);

  useEffect(() => {
    if (sortBy && sortBy.id) {
      toggleSortBy(sortBy.id, sortBy.desc, false);
    }
  }, [sortBy]);

  useEffect(() => {
    if (pageIndex === pageCount - 1 && hasFetchMore()) {
      fetchMore(true);
    }
    if (pageIndex > pageCount - 1) gotoPage(0);
  }, [pageIndex, pageCount]);

  const selectableItem = (column) => column.id === 'selection';
  const [activeIndex, setActiveIndex] = React.useState(0);
  const changeActiveItem = (index) => {
    setActiveIndex(index);
    setPageSize((index + 1) * 10);
  };
  return (
    <>
      <PaginationContainer>
        <Pagination
          {...{
            pageIndex,
            pageOptions,
            canNextPage,
            canPreviousPage,
            nextPage,
            previousPage,
            gotoPage
          }}
          previousText="Previous"
          nextText="Next"
        />
        <StyledSingleSelect
          label={undefined}
          dropdownText="Select.."
          activeIndex={activeIndex}
          dropdownData={[10, 20, 30, 40, 50].map((n) => ({
            Key: n,
            Value: `Show ${n}`
          }))}
          onClickSelectItem={changeActiveItem}
          withSortButton={false}
        />
      </PaginationContainer>
      <TableContainer>
        <Table hasSelectBox={hasSelectBox} {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableHeadItem
                    isSelection={selectableItem(column)}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <TableHeadContent isSelection={selectableItem(column)}>
                      <SortContainer
                        isSelection={selectableItem(column)}
                        onClick={() => {
                          const sortdirection =
                            column.isSortedDesc === undefined
                              ? false
                              : column.isSortedDesc
                              ? null
                              : true;
                          if (column.id !== 'selection') {
                            onSort({ desc: sortdirection, id: column.id });
                          }
                        }}
                      >
                        <span>{column.render('Header')}</span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <>{sortDown}</>
                          ) : (
                            <>{sortUp}</>
                          )
                        ) : (
                          column.id !== 'selection' && <HiddenSVGPlaceHolder />
                        )}
                      </SortContainer>
                    </TableHeadContent>
                  </TableHeadItem>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <TableData
                        isSelection={selectableItem(cell.column)}
                        {...cell.getCellProps()}
                      >
                        <InnerTableData>
                          {cell.column.Header === 'Name' &&
                            type === 'Organizations' &&
                            organisation}
                          {cell.column.Header === 'Name' &&
                            type === 'Customers' &&
                            organisation}
                          {cell.column.Header === 'Market' &&
                            type === 'Organizations' && (
                              <FlagFlexbox>
                                <GetFlag countryName={cell.value} />
                              </FlagFlexbox>
                            )}
                          {cell.column.Header === 'Market' &&
                            type === 'Customers' && (
                              <FlagFlexbox>
                                <GetFlag countryName={cell.value} />
                              </FlagFlexbox>
                            )}
                          <span>{cell.render('Cell')}</span>
                        </InnerTableData>
                      </TableData>
                    );
                  })}
                </TableRow>
              );
            })}
          </tbody>
        </Table>
        {loading && (
          <div>
            {new Array(4).fill(true).map((j, i) => (
              <Card key={i} label={undefined} loading={true} />
            ))}
          </div>
        )}
      </TableContainer>
      <PaginationContainer>
        <Pagination
          {...{
            pageIndex,
            pageOptions,
            canNextPage,
            canPreviousPage,
            nextPage,
            previousPage,
            gotoPage
          }}
          previousText="Previous"
          nextText="Next"
        />
        <StyledSingleSelect
          label={undefined}
          dropdownText="Select.."
          activeIndex={activeIndex}
          dropdownData={[10, 20, 30, 40, 50].map((n) => ({
            Key: n,
            Value: `Show ${n}`
          }))}
          onClickSelectItem={changeActiveItem}
          withSortButton={false}
        />
      </PaginationContainer>
    </>
  );
};

export default ListDocument;
